import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AndroidLogo,
  Cube,
  DownloadSimple,
  Package,
  Plus,
  RocketLaunch,
} from "@phosphor-icons/react";
import { useAuth } from "../../app/auth";
import { Drawer, MetricStrip, Modal, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { Application, ApplicationRelease, Device } from "../../lib/types";

function ApplicationModal({ onClose }: { onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [name, setName] = useState("");
  const [packageName, setPackageName] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useMutation({
    mutationFn: () => request("/api/applications", { method: "POST", body: JSON.stringify({ name, packageName, description }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["applications"] });
      notify("应用已加入目录", name);
      onClose();
    },
  });
  return (
    <Modal title="添加应用" description="登记经过验证的 Android 应用元数据。" onClose={onClose}
      footer={<><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={mutation.isPending || !name.trim() || !packageName.trim()} onClick={() => mutation.mutate()}>添加应用</button></>}>
      <div className="form-stack">
        <label><span>应用名称</span><input type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="例如 XMirror Workphone" /></label>
        <label><span>Android 包名</span><input type="text" value={packageName} onChange={(event) => setPackageName(event.target.value)} placeholder="com.example.application" /></label>
        <label><span>说明</span><textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
        {mutation.isError && <p className="form-error">添加失败，请检查包名格式或重复项。</p>}
      </div>
    </Modal>
  );
}

function ReleaseModal({ application, onClose }: { application: Application; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [versionName, setVersionName] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [artifactUrl, setArtifactUrl] = useState("");
  const [checksumSha256, setChecksumSha256] = useState("");
  const mutation = useMutation({
    mutationFn: () => request(`/api/applications/${application.id}/releases`, { method: "POST", body: JSON.stringify({ versionName, versionCode, artifactUrl, checksumSha256 }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["applications"] });
      notify("发布版本已登记", `${application.name} ${versionName}`);
      onClose();
    },
  });
  return (
    <Modal title="登记发布版本" description={`${application.name} · ${application.packageName}`} onClose={onClose}
      footer={<><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={mutation.isPending || !versionName || !versionCode || checksumSha256.length !== 64} onClick={() => mutation.mutate()}>保存版本</button></>}>
      <div className="form-stack">
        <div className="form-columns"><label><span>版本名称</span><input type="text" value={versionName} onChange={(event) => setVersionName(event.target.value)} placeholder="1.4.0" /></label><label><span>版本号</span><input type="text" value={versionCode} onChange={(event) => setVersionCode(event.target.value)} placeholder="10400" /></label></div>
        <label><span>制品 HTTPS 地址</span><input type="text" value={artifactUrl} onChange={(event) => setArtifactUrl(event.target.value)} placeholder="https://..." /></label>
        <label><span>SHA-256 校验值</span><textarea rows={2} value={checksumSha256} onChange={(event) => setChecksumSha256(event.target.value.trim())} placeholder="64 位十六进制摘要" /></label>
        <p className="operation-note">Connect 仅保存经过校验的制品元数据；构建和签名仍由工作手机发布流程负责。</p>
        {mutation.isError && <p className="form-error">版本保存失败，请检查地址、校验值和版本号。</p>}
      </div>
    </Modal>
  );
}

function DeploymentModal({ release, devices, onClose }: { release: ApplicationRelease; devices: Device[]; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => request(`/api/application-releases/${release.id}/deployments`, { method: "POST", body: JSON.stringify({ targetDeviceIds: selected, requiresApproval, reason }) }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: ["applications"] }), queryClient.invalidateQueries({ queryKey: ["tasks"] })]);
      notify(requiresApproval ? "应用发布已提交审批" : "应用发布任务已创建", `${release.versionName} · ${selected.length} 台设备`);
      onClose();
    },
  });
  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  return (
    <Modal title={`部署 ${release.versionName}`} description="应用部署会复用设备任务和审批状态机。" onClose={onClose}
      footer={<><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={mutation.isPending || !selected.length || (requiresApproval && !reason.trim())} onClick={() => mutation.mutate()}><RocketLaunch size={16} />创建部署</button></>}>
      <div className="form-stack">
        <div><div className="field-label"><span>目标设备</span><button onClick={() => setSelected(devices.map((device) => device.id))}>全选</button></div><div className="device-picker">{devices.map((device) => <label key={device.id}><input type="checkbox" checked={selected.includes(device.id)} onChange={() => toggle(device.id)} /><span><strong>{device.info?.model || `设备 #${device.id}`}</strong><small>{device.account?.username || device.info?.deviceSn || "未绑定"}</small></span><StatusTag value={device.status} /></label>)}</div></div>
        <label className="check-row"><input type="checkbox" checked={requiresApproval} onChange={(event) => setRequiresApproval(event.target.checked)} /><span><strong>需要审批</strong><small>建议生产环境应用发布始终经过审批。</small></span></label>
        {requiresApproval && <label><span>发布理由</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></label>}
        {mutation.isError && <p className="form-error">部署创建失败，请检查发布版本和目标设备。</p>}
      </div>
    </Modal>
  );
}

function ApplicationDetail({ application, devices, onClose }: { application: Application; devices: Device[]; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [deploymentRelease, setDeploymentRelease] = useState<ApplicationRelease | null>(null);
  const deploymentAction = useMutation({
    mutationFn: (input: { id: string; action: "pause" | "rollback" }) =>
      request(`/api/application-deployments/${input.id}/${input.action}`, {
        method: "POST",
        body: JSON.stringify({
          reason: input.action === "pause" ? "管理端人工暂停" : "管理端人工回滚",
          requiresApproval: true,
        }),
      }),
    onSuccess: async (_, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["applications"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
      ]);
      notify(input.action === "pause" ? "部署已暂停" : "回滚任务已提交审批");
      onClose();
    },
  });
  return (
    <Drawer title={application.name} subtitle={application.packageName} onClose={onClose}>
      <div className="application-profile"><AndroidLogo size={29} /><span><strong>{application.description || "Android 企业应用"}</strong><small>{application.releases.length} 个发布版本 · {application.deployments.length} 次部署</small></span><StatusTag value={application.status} /></div>
      <button className="primary-button drawer-action" onClick={() => setReleaseOpen(true)}><Plus size={16} />登记新版本</button>
      <section className="drawer-section"><h3>发布版本</h3><div className="release-list">{application.releases.map((release) => <div key={release.id}><Package size={20} /><span><strong>{release.versionName}</strong><small>版本号 {release.versionCode} · {formatDateTime(release.createdAt)}</small></span><button title="部署此版本" onClick={() => setDeploymentRelease(release)}><RocketLaunch size={16} /></button></div>)}{!application.releases.length && <p className="inline-empty">尚未登记发布版本。</p>}</div></section>
      <section className="drawer-section"><h3>最近部署</h3><div className="result-list">{application.deployments.map((deployment) => <div key={deployment.id}><span><strong>部署 #{deployment.id}</strong><small>{deployment.targetCount} 台设备 · 任务 #{deployment.taskId || "—"}</small></span><StatusTag value={deployment.status} /><span className="row-actions">{!["paused", "succeeded", "failed", "cancelled", "rolled_back"].includes(deployment.status) && <button disabled={deploymentAction.isPending} onClick={() => deploymentAction.mutate({ id: deployment.id, action: "pause" })}>暂停</button>}{application.releases.length > 1 && deployment.status !== "rolled_back" && <button disabled={deploymentAction.isPending} onClick={() => deploymentAction.mutate({ id: deployment.id, action: "rollback" })}>回滚</button>}</span></div>)}{!application.deployments.length && <p className="inline-empty">尚无部署记录。</p>}</div>{deploymentAction.isError && <p className="form-error">操作失败；首个版本不可回滚，终态部署不可暂停。</p>}</section>
      {releaseOpen && <ReleaseModal application={application} onClose={() => setReleaseOpen(false)} />}
      {deploymentRelease && <DeploymentModal release={deploymentRelease} devices={devices} onClose={() => setDeploymentRelease(null)} />}
    </Drawer>
  );
}

export function ApplicationsPage() {
  const applicationsQuery = useApiQuery<Application[]>(["applications"], "/api/applications");
  const devicesQuery = useApiQuery<Device[]>(["devices", "application-picker"], "/api/devices?limit=200");
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Application | null>(null);
  const applications = applicationsQuery.data || [];
  if (applicationsQuery.isError || devicesQuery.isError) return <QueryErrorState onRetry={() => { void applicationsQuery.refetch(); void devicesQuery.refetch(); }} />;
  return (
    <div className="module-page">
      <MetricStrip metrics={[
        { label: "应用目录", value: applications.length, note: "已登记应用" },
        { label: "发布版本", value: applications.reduce((sum, app) => sum + app.releases.length, 0), note: "通过制品校验" },
        { label: "部署记录", value: applications.reduce((sum, app) => sum + app.deployments.length, 0), note: "关联设备任务", tone: "metric-blue" },
        { label: "设备清单", value: devicesQuery.data?.length || 0, note: "可作为部署目标" },
      ]} />
      <section className="table-panel">
        <header className="module-toolbar"><div><h2 className="toolbar-title">企业应用目录</h2><p className="toolbar-subtitle">版本元数据、制品校验和设备部署统一管理。</p></div><button className="primary-button" onClick={() => setCreateOpen(true)}><Plus size={16} />添加应用</button></header>
        <div className="application-grid">{applications.map((application) => <article key={application.id} onClick={() => setSelected(application)}><div className="app-mark"><Cube size={27} /></div><span><h3>{application.name}</h3><code>{application.packageName}</code><p>{application.description || "未填写应用说明"}</p></span><div className="app-version"><small>最新版本</small><strong>{application.releases[0]?.versionName || "—"}</strong><em>{application.deployments.length} 次部署</em></div><DownloadSimple size={18} /></article>)}{!applicationsQuery.isPending && !applications.length && <div className="empty-grid-state">尚未登记应用。</div>}</div>
      </section>
      {createOpen && <ApplicationModal onClose={() => setCreateOpen(false)} />}
      {selected && <ApplicationDetail application={selected} devices={devicesQuery.data || []} onClose={() => setSelected(null)} />}
    </div>
  );
}
